import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, Smartphone, QrCode, CheckCircle } from "lucide-react";
import { MFAEnrollment } from "@/components/settings/MFAEnrollment";

interface ForcedMFAEnrollmentProps {
  isOpen: boolean;
  onComplete: () => void;
}

export const ForcedMFAEnrollment = ({ isOpen, onComplete }: ForcedMFAEnrollmentProps) => {
  const [currentStep, setCurrentStep] = useState<"intro" | "setup" | "complete">("intro");
  const [progress, setProgress] = useState(0);

  const handleStart = () => {
    setCurrentStep("setup");
    setProgress(50);
  };

  const handleEnrollmentComplete = () => {
    setCurrentStep("complete");
    setProgress(100);
    setTimeout(() => {
      onComplete();
    }, 2000);
  };

  const renderIntroStep = () => (
    <Card className="border-0 shadow-none">
      <CardHeader className="text-center">
        <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <Shield className="w-8 h-8 text-blue-600" />
        </div>
        <CardTitle className="text-xl">Secure Your Account</CardTitle>
        <CardDescription className="text-base">
          Two-factor authentication is required to access this application
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert>
          <Shield className="h-4 w-4" />
          <AlertDescription>
            This additional security step helps protect your account from unauthorized access.
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <Smartphone className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <h4 className="font-medium">Install an Authenticator App</h4>
              <p className="text-sm text-muted-foreground">
                Download Google Authenticator, Authy, or Microsoft Authenticator
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <QrCode className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium">Scan QR Code</h4>
              <p className="text-sm text-muted-foreground">
                Use your authenticator app to scan the QR code we'll generate
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <CheckCircle className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <h4 className="font-medium">Verify Setup</h4>
              <p className="text-sm text-muted-foreground">Enter a verification code to complete the setup</p>
            </div>
          </div>
        </div>

        <Button onClick={handleStart} className="w-full" size="lg">
          Set Up Two-Factor Authentication
        </Button>
      </CardContent>
    </Card>
  );

  const renderSetupStep = () => <MFAEnrollment onComplete={handleEnrollmentComplete} />;

  const renderCompleteStep = () => (
    <Card className="border-0 shadow-none">
      <CardHeader className="text-center">
        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <CardTitle className="text-xl text-green-700">Setup Complete!</CardTitle>
        <CardDescription className="text-base">
          Your account is now secured with two-factor authentication
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            You'll now be prompted for a verification code each time you sign in. Keep your authenticator app
            accessible.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );

  return (
    <Dialog open={isOpen} onOpenChange={() => {}} modal>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto [&>button]:hidden">
        <DialogHeader>
          <DialogTitle className="sr-only">
            {currentStep === "intro" && "MFA Setup Required"}
            {currentStep === "setup" && "Setting Up MFA"}
            {currentStep === "complete" && "MFA Setup Complete"}
          </DialogTitle>
          <DialogDescription className="sr-only">Complete MFA setup to secure your account</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Progress value={progress} className="w-full" />

          {currentStep === "intro" && renderIntroStep()}
          {currentStep === "setup" && renderSetupStep()}
          {currentStep === "complete" && renderCompleteStep()}
        </div>
      </DialogContent>
    </Dialog>
  );
};
